import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Trash2, X } from "lucide-react";
import { Button } from "../../components/ui/button/index.js";
import { AlertDialog, AlertDialogDescription, AlertDialogTitle } from "../../components/ui/alert-dialog/index.js";
import {
  contactsModalFooterClass,
  contactsModalHeaderClass,
} from "../contacts/contacts-dialog-presentation.js";
import { contactsBulkBarClass } from "../contacts/contacts-table-presentation.js";
import { CampaignModalContent } from "./CampaignModalContent.js";

export function CampaignsBulkActionsBar({
  selectedCount,
  busy,
  onClearSelection,
  onDeleteSelected,
}: {
  selectedCount: number;
  busy: boolean;
  onClearSelection: () => void;
  onDeleteSelected: () => Promise<void>;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (selectedCount <= 0) {
    return null;
  }

  return (
    <>
      <div
        className={contactsBulkBarClass}
        role="region"
        aria-label={t("campaigns.bulk.title")}
        data-testid="campaigns-bulk-bar"
      >
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[0.8125rem] font-semibold text-foreground">
            {t("campaigns.bulk.selected", { count: selectedCount })}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8"
            onClick={onClearSelection}
            data-testid="campaigns-bulk-clear"
          >
            <X size={14} aria-hidden="true" />
            {t("campaigns.bulk.clearSelection")}
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="destructive"
            size="sm"
            disabled={busy}
            onClick={() => setConfirmDelete(true)}
            data-testid="campaigns-bulk-delete"
          >
            {busy ? (
              <Loader2 size={14} className="animate-spin" aria-hidden="true" />
            ) : (
              <Trash2 size={14} aria-hidden="true" />
            )}
            {t("campaigns.bulk.delete")}
          </Button>
        </div>
      </div>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <CampaignModalContent>
          <div className={contactsModalHeaderClass}>
            <AlertDialogTitle>{t("campaigns.bulk.deleteTitle", { count: selectedCount })}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("campaigns.bulk.deleteBody", { count: selectedCount })}
            </AlertDialogDescription>
          </div>
          <div className={contactsModalFooterClass}>
            <Button type="button" variant="ghost" onClick={() => setConfirmDelete(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={busy}
              onClick={() => {
                void onDeleteSelected().finally(() => setConfirmDelete(false));
              }}
            >
              {t("campaigns.bulk.delete")}
            </Button>
          </div>
        </CampaignModalContent>
      </AlertDialog>
    </>
  );
}
